import { useEffect, useState } from "react";
import { fetchCached } from "@/lib/userCache";

/**
 * BadgeRow — renders a user's displayed badges inline (max 3 per spec).
 * Use anywhere user identity is shown (leaderboard rows, chat messages, profile header).
 * V2.5.2 — uses fetchCached so a leaderboard with 50 rows doesn't fire 50 GETs per user.
 */
export default function BadgeRow({ userId, size = "sm", className = "" }) {
  const [badges, setBadges] = useState([]);
  const [catalog, setCatalog] = useState({});

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    fetchCached("badges", `/badges/user/${userId}`).then(data => {
      if (cancelled) return;
      setBadges(data.displayed || []);
      setCatalog(data.catalog || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  if (!badges.length) return null;
  const dim = size === "lg" ? 22 : size === "md" ? 16 : 12;
  // V2.9.6 — Supreme badges (1-of-1 animated) render at ~1.7× scale so the animation is actually visible.
  const supremeDim = Math.round(dim * 1.7);
  return (
    <span className={`inline-flex items-center gap-0.5 ${className}`} data-testid={`badge-row-${userId}`}>
      {badges.map(bid => {
        const c = catalog[bid] || { name: bid };
        const isSupreme = c.kind === "supreme" || (c.image_url || "").endsWith(".mp4");
        if (isSupreme && c.image_url) {
          return (
            <span key={bid} title={`${c.name} [1 of 1]`}
              className="relative inline-block ring-2 ring-amber-400 rounded-full overflow-hidden shadow-[0_0_8px_rgba(251,191,36,0.6)]"
              style={{ width: supremeDim, height: supremeDim }}>
              <video src={c.image_url} autoPlay loop muted playsInline preload="auto"
                style={{ width: supremeDim, height: supremeDim, objectFit: "cover" }}/>
            </span>
          );
        }
        return c.image_url
          ? <img key={bid} src={c.image_url} alt={c.name} title={c.name}
              style={{ width: dim, height: dim, objectFit: "contain" }}
              className="inline-block"/>
          : <span key={bid} title={c.name}
              className={`inline-flex items-center justify-center rounded text-[8px] font-black uppercase text-${c.color || "amber"}-200 bg-${c.color || "amber"}-500/20 border border-${c.color || "amber"}-500/40`}
              style={{ width: dim, height: dim }}>
              {(c.name || bid).slice(0, 1)}
            </span>;
      })}
    </span>
  );
}
